import { useState } from 'react';
import { Check, ExternalLink, Send, X } from 'lucide-react';
import { format } from 'date-fns';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { AtsBadge, ScoutPostingStatusBadge } from '@/components/scout/ScoutStatusBadge';
import {
  usePromoteScoutPostings,
  useReviewScoutPostings,
  useScoutPosting,
} from '@/hooks/useScout';
import type { ScoutPostingDetail } from '@repo/types';

/**
 * Review one scouted posting.
 *
 * A sheet rather than its own page: this is a triage queue, and navigating away
 * loses both your place in the list and the multi-select you were building. The
 * reviewer opens a row, decides, and carries on down the list.
 */
export function ScoutPostingSheet({
  postingId,
  onOpenChange,
}: {
  postingId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: posting, isLoading } = useScoutPosting(postingId);

  return (
    <Sheet open={Boolean(postingId)} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        {isLoading || !posting ? (
          <div className="space-y-4 p-6">
            <Skeleton className="h-7 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <PostingDetail posting={posting} onDone={() => onOpenChange(false)} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function PostingDetail({
  posting,
  onDone,
}: {
  posting: ScoutPostingDetail;
  onDone: () => void;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const review = useReviewScoutPostings();
  const promote = usePromoteScoutPostings();
  const busy = review.isPending || promote.isPending;

  const promoted = Boolean(posting.promotedAt);
  // A posting whose URL is the page the agent was reading is a listing link,
  // not a job link. Promoting it hands the fetcher a search page, so it is
  // called out rather than left for the reviewer to spot by eye.
  const urlIsListingPage =
    Boolean(posting.targetUrl) && normalize(posting.url) === normalize(posting.targetUrl as string);

  return (
    <>
      <SheetHeader className="px-6">
        <SheetTitle className="pr-6 leading-snug">{posting.title}</SheetTitle>
        <SheetDescription>
          {[posting.company || posting.targetCompany, posting.location].filter(Boolean).join(' · ') ||
            'No company or location captured'}
        </SheetDescription>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <ScoutPostingStatusBadge status={posting.status} promoted={promoted} />
          {posting.targetAts && <AtsBadge ats={posting.targetAts} />}
          {posting.programNames.map((name) => (
            <Badge key={name} variant="outline" className="font-normal">
              {name}
            </Badge>
          ))}
        </div>
      </SheetHeader>

      <div className="space-y-5 px-6 pb-6">
        {urlIsListingPage && (
          <Alert variant="destructive">
            <AlertDescription>
              This posting&apos;s link is the career site&apos;s own listing page, not an
              individual job. Promoting it would send the fetcher a search page — reject it
              unless you can supply the real URL.
            </AlertDescription>
          </Alert>
        )}

        <Field label="Posting URL">
          <a
            href={posting.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-start gap-1 break-all text-sm hover:underline"
          >
            {posting.url}
            <ExternalLink className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
          </a>
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Posted">{posting.postedAt || '—'}</Field>
          <Field label="Scouted">{format(new Date(posting.scoutedAt), 'd MMM yyyy, HH:mm')}</Field>
          <Field label="Type">{posting.jobType || '—'}</Field>
          <Field label="Work mode">{posting.workMode || '—'}</Field>
          <Field label="Salary">{posting.salary || '—'}</Field>
          <Field label="Source site">
            {posting.targetCompany || '—'}
          </Field>
        </div>

        <Field label="Description">
          {posting.description ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{posting.description}</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nothing captured — the listing showed no summary text.
            </p>
          )}
        </Field>

        {(posting.reviewedAt || promoted) && (
          <>
            <Separator />
            <div className="space-y-1 text-xs text-muted-foreground">
              {posting.reviewedAt && (
                <p>
                  {posting.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                  {posting.reviewedByName || 'someone'} on{' '}
                  {format(new Date(posting.reviewedAt), 'd MMM yyyy, HH:mm')}
                  {posting.reviewNote ? ` — ${posting.reviewNote}` : ''}
                </p>
              )}
              {promoted && (
                <p>
                  Sent to the scraping queue on{' '}
                  {format(new Date(posting.promotedAt as string), 'd MMM yyyy, HH:mm')}
                  {posting.promotedQueueId ? ` (queue item ${posting.promotedQueueId.slice(0, 8)})` : ''}
                </p>
              )}
            </div>
          </>
        )}

        <Separator />

        {/* The agent's verbatim output. This is how you tell a bad extraction
            from a bad listing — e.g. a company field that captured a business
            unit rather than the employer. */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {showRaw ? 'Hide' : 'Show'} what the agent returned
          </button>
          {showRaw && (
            <pre className="max-h-72 overflow-auto rounded-md border bg-muted/40 p-3 text-[11px] leading-relaxed">
              {JSON.stringify(posting.raw, null, 2)}
            </pre>
          )}
        </div>

        {!promoted && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                review.mutate(
                  { postingIds: [posting.id], status: 'approved' },
                  { onSuccess: onDone }
                )
              }
            >
              <Check className="size-4" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() =>
                review.mutate(
                  { postingIds: [posting.id], status: 'rejected' },
                  { onSuccess: onDone }
                )
              }
            >
              <X className="size-4" />
              Reject
            </Button>
            {posting.status === 'approved' && (
              <Button
                size="sm"
                disabled={busy}
                onClick={() =>
                  promote.mutate({ postingIds: [posting.id] }, { onSuccess: onDone })
                }
              >
                <Send className="size-4" />
                Send to queue
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

/** Compare URLs ignoring trailing slash and case, which differ meaninglessly. */
function normalize(url: string): string {
  return url.trim().toLowerCase().replace(/\/+$/, '');
}
